import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Loader from "Common/Loader";
import DataGrid, {
  Pager,
  Paging,
  SearchPanel,
  Column,
  Summary,
  TotalItem,
  Toolbar,
  Item,
  Scrolling,
} from "devextreme-react/cjs/data-grid";
import { Card, Pagination } from "react-bootstrap";
import moment from 'moment';
import CustomStore from 'devextreme/data/custom_store';
import DateRangeBox from 'devextreme-react/date-range-box';
import TextBox, { type TextBoxTypes } from 'devextreme-react/text-box';
import { Button } from 'devextreme-react/button';
import { FaFileExcel } from "react-icons/fa";
import { getAllSalesInventoryData } from '../services/salesInventory-services';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import type { ValueChangedEvent } from 'devextreme/ui/date_range_box';
import notistack from 'modules/core/notistack';
import './index.css'
import { HasPermission } from 'Layout/Permissions/permissionsCache';
import { formatDateToIST } from 'helpers/dateFormat';

const SalesInventoryDataList = () => {
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageSize: 10, pageNumber: 1 });
  const [totalAmount, setTotalAmount] = useState(0)
  const [totals, setTotals] = useState({
    sellingPrice: 0,
    margin: 0 || '',
    buyingPrice: 0,
  });
  const now = new Date();
  const msInDay = 1000 * 60 * 60 * 24;
  const initialValue: [Date, Date] = [
    new Date(now.getTime() - msInDay * 3),
    new Date(now.getTime() + msInDay * 3),
  ];
  const [salesInventoryData, setSalesInventoryData] = useState<any[]>()
  const dataGridRef = useRef<DataGrid>(null);
  const [selectedRange, setSelectedRange] = useState<[Date, Date] | null>(initialValue);
  const [searchText, setSearchText] = useState('');

  function formatDateTime(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }



  const dataSource = new CustomStore({
    key: 'id',
    load: async (loadOptions) => {
      try {
        const search = JSON.parse(JSON.stringify(loadOptions));
        let parameterList = new URLSearchParams();
        for (const [key, value] of Object.entries(search)) {
          parameterList.append(key, JSON.stringify(value));
        }
        if (selectedRange) {
          parameterList.append("FromDate", formatDateTime(selectedRange[0]));
          parameterList.append("ToDate", formatDateTime(selectedRange[1]));

        }

        if (searchText) {
          parameterList.append("Search", searchText);
        }
        const response = await getAllSalesInventoryData(parameterList);

        // return {
        //   data: response?.data || [],
        //   totalCount: response?.data.length || 0,
        // };
        return response;

      } catch (error) {
        console.error("Data loading error:", error);
        return { data: [], totalCount: 0 };
      }
    }
  });


  const getAllSalesInventoryDataList = async () => {
    try {
      let parameterList = new URLSearchParams();

      if (selectedRange) {
        parameterList.append("FromDate", formatDateTime(selectedRange[0]));
        parameterList.append("ToDate", formatDateTime(selectedRange[1]));

      }

      if (searchText) {
        parameterList.append("Search", searchText);
      }
      const response = await getAllSalesInventoryData(parameterList);
      setSalesInventoryData(response.data)
      const totalSellingPrice = response?.data?.reduce?.((acc: any, item: any) => acc + (item.selingPrice || 0), 0).toFixed(2);
      const totalBuyingPrice = response?.data?.reduce?.((acc: any, item: any) => acc + (item.dpPrice || 0), 0).toFixed(2);
      const totalMarginRaw = totalSellingPrice - totalBuyingPrice;
      const totalMargin = isNaN(totalMarginRaw) || totalMarginRaw === 0
        ? "0.00"
        : totalMarginRaw.toFixed(2);
      setTotals({
        sellingPrice: totalSellingPrice,
        buyingPrice: totalBuyingPrice,
        margin: totalMargin,
      });
    } catch (error) {

    }
  }
  useEffect(() => {
    getAllSalesInventoryDataList()
  }, [selectedRange]);

  const handleExportExcel = () => {
    if (!salesInventoryData) return;

    const worksheet = XLSX.utils.json_to_sheet(salesInventoryData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Errors");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "Sales_Inventory_Export.xlsx");
  }

  const onCurrentValueChange = useCallback((e: ValueChangedEvent) => {
    const value = e.value as [Date, Date] | undefined;
    if (value && value[0] && value[1]) {
      const [startDate, endDate] = value;
      setSelectedRange([startDate, endDate]);
    } else {
      setSelectedRange(null);
    }
  }, []);

  useEffect(() => {
    getAllSalesInventoryDataList();
  }, [selectedRange, searchText]);

  return (
    <>
      {loading && <Loader />}
      <div className="page-content" style={{ maxWidth: '100vw', overflowX: 'auto' }}>
        <div className="container-fluid">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="m-0">Sales Inventory</h5>
            </Card.Header>

            <Card.Body>
              {HasPermission('SalesInventory.Read') &&
                <>
                  <div className="custom-toolbar w-auto d-flex flex-wrap gap-2 mb-3 ">
                    <div style={{ width: "100%", display: "flex" }} className='w-100 d-flex flex-end gap-3'>
                      <div style={{ display: 'flex', flexDirection: 'column' }} className='mt-1'>
                        <label>Search</label>
                        <TextBox
                          width={180}
                          placeholder="Search..."
                          value={searchText}
                          onValueChanged={(e) => setSearchText(e.value)}
                          onKeyDown={(e: any) => {
                            if (e.event.key === "Enter" || e.event.key === "Tab") {
                              dataGridRef.current?.instance.refresh();
                            }
                          }}
                        />
                      </div>

                      <div>
                        <DateRangeBox
                          width={300}
                          defaultValue={['', '']}
                          displayFormat="dd/MM/yyyy"
                          showClearButton={true}
                          acceptCustomValue={false}
                          onValueChanged={onCurrentValueChange}
                        />
                      </div>


                      <div className='mt-4'>

                        <Button
                          icon="https://img.favpng.com/18/7/13/scalable-vector-graphics-microsoft-excel-clip-art-portable-network-graphics-png-favpng-yynmZdMbFF2A9c7tcbZa6Ewv5.jpg"
                          hint="Export to Excel"
                          onClick={handleExportExcel}
                        />
                      </div>
                    </div>


                    <div style={{ width: "100%", display: "flex" }} className='w-100 d-flex flex-end gap-3'>
                      <div className='w-auto'>
                        <span className='fw-bold'>Total Selling Price:</span> ₹ {totals?.sellingPrice}
                      </div>
                      <div className='w-auto'>
                        <span className='fw-bold'>Margin:</span> ₹ {totals?.margin}
                      </div>
                      <div className='w-auto'>
                        <span className='fw-bold'>Buying Price:</span> ₹ {totals?.buyingPrice}
                      </div>
                    </div>

                  </div>

                  <div className='sales-inventory-grid'>
                    <DataGrid
                      dataSource={dataSource}
                      remoteOperations={{ groupPaging: true }}
                      showRowLines={true}
                      keyExpr="id"
                      ref={dataGridRef}
                      showBorders={true}
                      columnAutoWidth={true}
                      columnHidingEnabled={false}
                      width="100%"
                      onContentReady={(e) => {
                        const hasData = e.component.totalCount() > 0;
                        e.component.option("pager.visible", hasData);
                        e.component.option("paging.visible", hasData);
                      }}
                    >
                      <Scrolling
                        mode='standard'
                        useNative={true}
                        scrollByContent={true}
                        scrollByThumb={true}
                      />
                      <Pager
                        showPageSizeSelector={true}
                        allowedPageSizes={[10, 25, 50, 100]}
                        showNavigationButtons={true}
                        displayMode="full"
                        showInfo={true}
                      />
                      <Paging defaultPageSize={10} />

                      <Summary>
                        <TotalItem />
                      </Summary>

                      <Column dataField="invoiceNo" caption="Invoice No." dataType="number" width={150} alignment="center" />
                      <Column dataField="soDate" caption="Selling Date" dataType="string" width={200}
                        calculateDisplayValue={(rowData) => formatDateToIST(rowData.soDate)} alignment="center" />
                      <Column dataField="style" caption="Style No." dataType="string" width={150} alignment="center" />
                      <Column dataField="category" caption="Category" dataType="string" width={150} alignment="center" />
                      <Column dataField="diaWt" caption="Dia Wt" dataType="number" width={150} alignment="center" format="#,##0.000" />
                      <Column dataField="diaQty" caption="Dia Qty" dataType="number" width={150} alignment="center" />
                      <Column dataField="netWt" caption="Net Wt" dataType="number" width={150} alignment="center" format="#,##0.000" />
                      <Column dataField="grossWt" caption="Gross Wt" dataType="string" width={150} alignment="center" format="#,##0.000" />
                      <Column dataField="karat" caption="Metal Kt" dataType="string" width={150} alignment="center" />
                      <Column dataField="igiNo" caption="IGI No." dataType="string" width={150} alignment="center" />
                      <Column dataField="huid" caption="HUID" dataType="string" width={150} alignment="center" />
                      <Column dataField="dpPrice" caption="DP" dataType="number" format="#,##0.00" width={150} alignment="center" />
                      <Column dataField="selingPrice" caption="Selling Price" format="#,##0.00" dataType="number" width={150} alignment="center" />
                      <Column dataField="isSRP" caption="Is SRP" dataType="number" width={150} alignment="center" />
                    </DataGrid>
                  </div>
                </>
              }
            </Card.Body>
          </Card>
        </div>
      </div>

    </>
  );
};

export default SalesInventoryDataList;